// Women's Circle Event Detail — RSVP + Live Chat
import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, Users, MessageCircle,
  Send, Loader2, CheckCircle, XCircle, Shield, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../api/client';
import { AuthContext } from '../App';
import { GlobalNav } from '../components/GlobalNav';

const ICON_MAP = {
  shield: Shield,
  heart: () => <span className="text-lg">&#10084;</span>,
  sparkles: () => <span className="text-lg">&#10024;</span>,
  baby: () => <span className="text-lg">&#128118;</span>,
  globe: () => <span className="text-lg">&#127758;</span>,
  'message-circle': MessageCircle,
};

function StatusBadge({ status }) {
  const styles = {
    scheduled: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    live: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 animate-pulse',
    ended: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    canceled: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  };
  return (
    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full uppercase tracking-wide ${styles[status] || styles.scheduled}`}
      data-testid="circle-status-badge">
      {status === 'live' ? 'Live Now' : status}
    </span>
  );
}

function formatDate(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function WomensCirclePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch event
  const fetchEvent = useCallback(async () => {
    try {
      const res = await apiClient.get(`/her-space/circles/${eventId}`, { headers });
      setEvent(res.data.event);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Her Space is available to women accounts only.');
        navigate('/dashboard');
      } else if (err.response?.status === 404) {
        toast.error('Circle event not found');
        navigate('/her-space');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, token]);

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!event?.chat_allowed && event?.computed_status !== 'ended') return;
    try {
      setLoadingMsgs(true);
      const res = await apiClient.get(`/her-space/circles/${eventId}/messages?limit=100`, { headers });
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load messages', err);
    } finally {
      setLoadingMsgs(false);
    }
  }, [eventId, token, event?.chat_allowed, event?.computed_status]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  useEffect(() => {
    if (!event) return;
    if (event.chat_allowed || event.computed_status === 'ended') {
      fetchMessages();
    }
    // Poll for new messages during live events
    if (event.chat_allowed) {
      pollRef.current = setInterval(fetchMessages, 8000);
      return () => clearInterval(pollRef.current);
    }
  }, [event?.chat_allowed, event?.computed_status, fetchMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // RSVP
  const handleRSVP = async (status) => {
    setRsvpLoading(true);
    try {
      const res = await apiClient.post(`/her-space/circles/${eventId}/rsvp`, { rsvp_status: status }, { headers });
      setEvent(prev => ({ ...prev, viewer_rsvp: status, rsvp_count: res.data.rsvp_count }));
      toast.success(status === 'going' ? "You're going!" : 'RSVP updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  // Send message
  const handleSend = async (e) => {
    e.preventDefault();
    if (!msgInput.trim() || sendingMsg) return;
    setSendingMsg(true);
    try {
      const res = await apiClient.post(`/her-space/circles/${eventId}/messages`, { content: msgInput.trim() }, { headers });
      setMessages(prev => [...prev, res.data.message]);
      setMsgInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMsg(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen surface-page">
        <GlobalNav />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
        </div>
      </div>
    );
  }

  if (!event) return null;

  const isGoing = event.viewer_rsvp === 'going';
  const canChat = event.chat_allowed;
  const showChat = canChat || event.computed_status === 'ended';

  return (
    <div className="min-h-screen surface-page">
      <GlobalNav />
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        {/* Back */}
        <button onClick={() => navigate('/her-space')}
          className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition mb-5"
          data-testid="circle-back-btn">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Her Space</span>
        </button>

        {/* Event Card */}
        <div className="surface-card rounded-2xl p-6 shadow-sm mb-6" data-testid="circle-event-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <StatusBadge status={event.computed_status} />
              <span className="ml-2 px-2.5 py-1 text-xs font-medium rounded-full bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">
                {event.topic_label}
              </span>
            </div>
          </div>

          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-testid="circle-title">
            {event.title}
          </h1>
          {event.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
              {event.description}
            </p>
          )}

          <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400 mb-5">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {formatDate(event.start_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {formatTime(event.start_at)} - {formatTime(event.end_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              {event.rsvp_count} going
            </span>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
            Hosted by {event.host_label}
          </p>

          {/* RSVP buttons */}
          {event.computed_status === 'scheduled' || event.computed_status === 'live' ? (
            <div className="flex gap-3" data-testid="circle-rsvp-section">
              <button onClick={() => handleRSVP('going')} disabled={rsvpLoading || isGoing}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition ${
                  isGoing
                    ? 'bg-green-600 text-white cursor-default'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                } disabled:opacity-70`}
                data-testid="rsvp-going-btn">
                {rsvpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isGoing ? <CheckCircle className="w-4 h-4" /> : null}
                {isGoing ? "You're Going" : "I'm Going"}
              </button>
              {isGoing && (
                <button onClick={() => handleRSVP('not_going')} disabled={rsvpLoading}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
                  data-testid="rsvp-cancel-btn">
                  <XCircle className="w-4 h-4" />
                  Cancel
                </button>
              )}
            </div>
          ) : event.computed_status === 'canceled' ? (
            <p className="text-sm text-red-600 dark:text-red-400 font-medium">This circle has been canceled.</p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-500">This circle has ended.</p>
          )}
        </div>

        {/* Chat Section */}
        {showChat && (
          <div className="surface-card rounded-2xl shadow-sm overflow-hidden" data-testid="circle-chat-section">
            <div className="px-5 py-3 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-rose-500" />
                Circle Chat
                {event.computed_status === 'ended' && <span className="text-xs text-gray-400 font-normal">(read-only)</span>}
              </h2>
              {canChat && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto px-5 py-3 space-y-3" data-testid="circle-chat-messages">
              {loadingMsgs && messages.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : messages.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-8">
                  {canChat ? 'Be the first to share...' : 'No messages in this circle.'}
                </p>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="flex gap-3" data-testid={`chat-msg-${msg.id}`}>
                    <div className="w-8 h-8 rounded-full bg-rose-100 dark:bg-rose-900/40 flex items-center justify-center flex-shrink-0 text-xs font-bold text-rose-700 dark:text-rose-300">
                      {(msg.author_name || '?')[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{msg.author_name}</span>
                        <span className="text-xs text-gray-400">{formatTime(msg.ts)}</span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 break-words">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            {canChat && (
              <form onSubmit={handleSend} className="px-5 py-3 border-t border-gray-100 dark:border-slate-800 flex gap-2">
                <input
                  type="text"
                  value={msgInput}
                  onChange={(e) => setMsgInput(e.target.value)}
                  placeholder="Share your thoughts..."
                  maxLength={1000}
                  className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-300 dark:focus:ring-rose-500/40"
                  data-testid="circle-chat-input"
                />
                <button type="submit" disabled={sendingMsg || !msgInput.trim()}
                  className="px-4 py-2.5 rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="circle-chat-send-btn">
                  {sendingMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
